/**
 * one-stop/strings — the renderer's own UI chrome, in the page's language.
 *
 * WHY THIS FILE EXISTS (JAI-1154)
 * A `de-CH` page rendering "Find help", "1 inside" and "Registration open (6/40)"
 * speaks two languages at once. Those literals were hardcoded in the M1 JSX.
 * Here they are a keyed catalog resolved against the DOCUMENT's locale, so the
 * page speaks one language whichever one that is.
 *
 * THE PRECEDENCE RULE, AND IT IS NOT NEGOTIABLE
 * Authored config ALWAYS wins. Every block component reads its label from
 * `block.config` first (`configText(...)`, a locale map) and falls back to this
 * catalog only when the author left the key empty. This catalog is the
 * renderer's chrome — never a substitute for authored content, and never a
 * default that silently overrides what an admin typed.
 *
 * ui-config: a closed UI vocabulary, not data. No seed, mock or fictional
 * content is in this file — no persona, no wait time, no department, no event.
 * Every value here is a label this renderer would show over ANY tenant's data.
 *
 * ADDING A LOCALE is adding one entry to CATALOG. A missing key falls back to
 * the base language, then to `en-US`, so a partially translated catalog degrades
 * to a readable page rather than to a hole.
 *
 * SHIPPED LOCALES: `en-US`, `es-US`/`es`, and (JAI-1154) `de`, `de-CH`, `fr`,
 * `it` — the four whose catalogs live in `strings-eu.ts`, because they are data
 * and this file is the resolution rule.
 */
import { DE, DE_CH, FR, IT } from "./strings-eu";

/* ── the catalog ─────────────────────────────────────────────────────── */

/** ui-config */
const EN = {
  "page.unavailable": "This page isn’t available right now.",
  "page.unavailable.body": "Please try again in a moment.",

  "search.placeholder": "Say it in your own words…",
  "search.findHelp": "Find help",
  "search.clear": "Clear search",
  "search.clearShort": "Clear",
  "search.aria": "Search for help",
  "search.results.one": "{n} result for “{q}”",
  "search.results.other": "{n} results for “{q}”",
  "search.results.sub": "Walk in now, or book a time — whichever gets you seen sooner.",
  "search.noMatch.title": "Nothing matched “{q}”",
  "search.noMatch.body": "Try the words on your paperwork, or start from one of these:",
  "search.error": "Search isn’t available right now.",

  "busy.short-wait": "Short wait",
  "busy.busy": "Busy",
  "busy.very-busy": "Long wait",
  "busy.closed": "Closed",

  "queues.title": "Available queues",
  "queues.subtitle": "Walk in without an appointment — these are the waits at the moment.",
  "queues.empty": "Live wait times aren’t available right now.",
  "queues.join": "Join the line",
  "queues.book": "Book a time",
  "queues.minutes": "min",
  "queues.hours": "hr",
  "queues.walk": "{n} min walk",
  "queues.openUntil": "Open until {t}",
  "queues.opensNext": "Opens {t}",
  "queues.ahead": "{n} ahead",
  "queues.more.one": "{n} more desk in scope.",
  "queues.more.other": "{n} more desks in scope.",
  "queues.sort.shortest-wait": "Shortest wait first",
  "queues.sort.name": "By name",
  "queues.sort.location": "By location",
  "queues.sort.curated": "In the order set here",

  "appointments.title": "Rather not wait? Book a time",
  "appointments.subtitle": "The next openings this week.",
  "appointments.empty": "No bookable times are available right now.",
  "appointments.cta": "Book Now",
  "appointments.none": "No openings in this window",
  "appointments.more.one": "{n} more service in scope.",
  "appointments.more.other": "{n} more services in scope.",

  "events.title": "This week",
  "events.subtitle": "Workshops, fairs and drop-ins happening now.",
  "events.empty": "Nothing scheduled in this window.",
  "events.featured.eyebrow": "Featured",
  "events.featured.title": "Don’t miss this one",
  "events.featured.empty": "No event is featured right now.",
  "events.all": "All events",
  "events.cta": "Register",
  "events.more.one": "{n} more event in scope.",
  "events.more.other": "{n} more events in scope.",

  "reg.open": "Registration open",
  "reg.closed": "Registration closed",
  "reg.full": "Full",
  "reg.waitlist": "Waitlist",
  "reg.waitlist.count": "Waitlist — {n} waiting",
  "reg.not-required": "No registration needed",
  "reg.counts": "{label} ({a}/{b})",
  "reg.countsOnly": "{label} ({a} registered)",

  "directory.title": "Browse departments",
  "directory.titleCount": "Browse all {n} departments",
  "directory.subtitle": "If you already know who you need — every department, what it books, and how.",
  "directory.empty": "The department list isn’t available right now.",
  "directory.show": "Show",
  "directory.hide": "Hide",
  "directory.inside.one": "{n} inside",
  "directory.inside.other": "{n} inside",
  "directory.cap.walkIns": "Walk in",
  "directory.cap.books": "Book",
  "directory.cap.events": "Events",
  "directory.more": "{n} more in this part of the org.",

  "checklist.title": "This week",
  "checklist.empty": "You’re all set this week.",
  "checklist.more": "Show all {n}",
  "checklist.less": "Show fewer",
  "checklist.cta.join-queue": "Walk in",
  "checklist.cta.book-appointment": "Book",
  "checklist.cta.register-event": "Register",

  "identity.eyebrow": "Happening now",
  "identity.signedOut.title": "Sign in for your account",
  "identity.signedOut.button": "Continue with sign-in",
  "identity.findVisit": "Already in a line? Find my visit →",
  "identity.greeting": "Welcome back, {name}",
  "identity.greetingGeneric": "Welcome back",
  "identity.ahead": "ahead",
  "identity.ticketCta": "View my ticket",
  "identity.visitsCta": "My visits",
  "identity.degraded": "Some details are unavailable right now.",
  "identity.nextAppointment": "Next appointment",
  "identity.ticket.waiting": "In line at {queue}",
  "identity.ticket.called": "You’ve been called at {queue}",
  "identity.ticket.being-served": "Being served at {queue}",
  "identity.signOut": "Sign out",
  "identity.demo": "Demo",
  "identity.sessionEnded": "Your session has ended. Please sign in again.",

  "signin.title": "Sign in",
  "signin.chooser.body": "Choose who you’re signing in as.",
  "signin.chooser.loading": "Loading the sign-in options…",
  "signin.chooser.error": "We couldn’t load the sign-in options.",
  "signin.useEmail": "Sign in with your email instead",
  "signin.email.body": "We’ll email you a one-time code.",
  "signin.email.field": "Email",
  "signin.email.send": "Send me a code",
  "signin.email.sent": "If that address is on file, a code is on its way. It expires in {n} minutes.",
  "signin.email.code": "Enter the code",
  "signin.email.submit": "Sign in",
  "signin.email.resend": "Send another code",
  "signin.email.wait": "You can ask for another code in {n}s.",
  "signin.email.other": "Use a different address",
  "signin.error.generic": "That sign-in didn’t go through.",
  "signin.error.code": "That code isn’t right, or it has expired. Ask for a new one.",
  "signin.error.unavailable": "That sign-in isn’t available. Start again.",
  "signin.error.tooMany": "Too many tries. Ask for a new code.",

  /* Demo mode only. The chooser is already demo-gated — a tenant without demo
     personas never sees any of this — so these strings say "demo" out loud
     rather than dressing a prop up as the real thing. */
  "signin.demo.body": "This organisation has demo mode on. Sign in the way a student would, or pick a student from the list.",
  "signin.demo.asStudent": "Sign in as student",
  "signin.demo.asStudentHint": "See the portal exactly as {name} does.",
  "signin.demo.pickFromList": "Sign in (pick from list)",
  "signin.demo.pickFromListHint": "Choose any student and land straight in their portal.",
  "signin.demo.credentials": "Demo credentials — filled in for you",
  "signin.demo.password": "Password",
  "signin.demo.submit": "Sign in",
  "signin.demo.otherStudent": "Use a different student",
  "signin.demo.back": "Back",

  "myVisits.title": "Find my visit",
  "myVisits.eyebrow": "Lost the text?",
  "myVisits.body": "Look up anything you joined or booked as a guest, with the phone number you used.",
  "myVisits.field": "Mobile number",
  "myVisits.submit": "Text me my visits",

  "flow.join-queue.title": "Join the line",
  "flow.join-queue.cta": "Join the line",
  "flow.book-appointment.title": "Book a time",
  "flow.book-appointment.cta": "Book",
  "flow.register-event.title": "Register",
  "flow.register-event.cta": "Register",
  "flow.unconfigured": "This block has not been pointed at anything yet.",

  "faq.show": "Show",
  "faq.hide": "Hide",

  "notice.dismiss": "Dismiss",

  "cta.working": "Working…",
  "cta.retry": "Try again",
  "cta.error": "That didn’t go through.",
  "cta.pending.join": "Joining the line opens with the next drop",
  "cta.pending.book": "Booking opens with the next drop",
  "cta.pending.register": "Registering opens with the next drop",
  "cta.pending.lookup": "Looking up a visit opens with the next drop",
  "cta.pending.signIn": "Signing in opens with the next drop",
  "cta.preview": "Not live in preview",
  "cta.offline": "We couldn’t reach the server.",

  "visitor.legend": "Your details",
  "visitor.firstName": "First name",
  "visitor.lastName": "Last name",
  "visitor.email": "Email",
  "visitor.phone": "Mobile number",
  "visitor.phoneWhy": "So we can text you when it’s your turn.",
  "visitor.optional": "Optional",
  "visitor.incomplete": "Add your name and email to continue.",
  "visitor.incompleteName": "Add your name to continue.",
  "visitor.error.firstName": "Enter your first name.",
  "visitor.error.lastName": "Enter your last name.",
  "visitor.error.emailRequired": "Enter your email address.",
  "visitor.error.email": "That doesn’t look like an email address.",
  "visitor.error.phone": "That doesn’t look like a phone number.",
  "visitor.cancel": "Cancel",

  "queue.joined.eyebrow": "You’re in the line",
  "queue.joined.ticket": "Ticket {ref}",
  "queue.joined.ahead": "ahead of you",
  "queue.joined.wait": "About {n} min",
  "queue.joined.code": "Check-in code {code}",
  "queue.already": "You’re already in this line.",
  "queue.closed": "This desk has closed.",
  "queue.leave": "Leave the line",
  "book.hold.eyebrow": "Time held for you",
  "book.holdUntil": "Held until {t}",
  "book.holdLeft": "{t} left",
  "book.confirm": "Confirm this time",
  "book.confirmed": "Appointment confirmed",
  "book.taken": "That time has just been taken.",
  "book.expired": "Your hold ran out.",
  "book.maybeBooked": "We may already have your booking — check your email.",
  "book.pickAnother": "Pick another time",
  "book.otherTimes": "Other times that day",
  "book.noOtherTimes": "Nothing else is free that day.",
  "event.registered": "You’re registered",
  /* LOCAL PATCH (platform side, 2026-10-04 JAI-2279): neutral duplicate-registration copy. CD to absorb. */
  "event.alreadyRegistered": "You’re already registered",
  "event.alreadyRegistered.body": "You’re already registered for this event. Check your email for your confirmation.",
  "ref": "Reference {ref}",
  /* The anchor's accessible name when the checkout handed back a manage link.
     "Reference VYG-6CPZ4G" says what the link IS, not where it goes — so the name
     carries the destination instead. The new-tab clause is UNCONDITIONAL because
     `target="_blank"` is on BOTH branches of `ManagedReference` (0053 §2): one
     string, one attribute, nothing to drift. If that target ever becomes
     conditional, this value has to become two. */
  "ref.manage.aria": "Manage booking {ref} (opens in a new tab)",
  "copy.action": "Copy",
  "copy.done": "Copied",
  "copy.ticket": "Copy ticket {ref}",
  "copy.code": "Copy check-in code {code}",
  "copy.ref": "Copy reference {ref}",

  /* The signed-in booking handoff (JAI-1155 / the grant flow). The second click
     is what keeps the browser's user gesture on the form submit, so this copy
     names the tab it opens rather than pretending the tap booked anything. */
  "handoff.title": "Continue to booking",
  "handoff.body.session": "Your booking opens in a new tab, signed in as you. This page stays open.",
  "handoff.body.guest": "Your booking opens in a new tab. This page stays open.",
  "handoff.continue": "Continue to booking",
  "handoff.preparing": "Getting your booking ready…",
  "handoff.opened": "Your booking is open in the other tab.",
  "handoff.expired": "That booking link timed out before it was used.",
  "handoff.new": "Get a new booking link",
  "handoff.error": "Booking isn’t available right now.",
  "handoff.error.wait": "Too many tries. Try again in {n}s.",

  "block.unsupported.title": "Unsupported block",
  "block.unsupported.body":
    "This block type has no renderer, so it will not appear on the published page.",
} as const;

export type StringKey = keyof typeof EN;

/**
 * The key SPACE, as data. Exported so the locale test can enumerate what every
 * catalog owes without reading keys back through the resolver — which would hide
 * a missing key behind its own English fallback, i.e. behind the exact
 * behaviour the test exists to catch.
 */
export const STRING_KEYS = Object.keys(EN) as StringKey[];

/** ui-config — es-US, the seed documents' second supported locale. */
const ES: Partial<Record<StringKey, string>> = {
  "page.unavailable": "Esta página no está disponible en este momento.",
  "page.unavailable.body": "Vuelve a intentarlo en un momento.",

  "search.placeholder": "Dilo con tus propias palabras…",
  "search.findHelp": "Buscar ayuda",
  "search.clear": "Borrar la búsqueda",
  "search.clearShort": "Borrar",
  "search.aria": "Buscar ayuda",
  "search.results.one": "{n} resultado para «{q}»",
  "search.results.other": "{n} resultados para «{q}»",
  "search.results.sub": "Entra sin cita o reserva una hora: lo que te atienda antes.",
  "search.noMatch.title": "Nada coincide con «{q}»",
  "search.noMatch.body": "Prueba con las palabras de tus documentos, o empieza por una de estas:",
  "search.error": "La búsqueda no está disponible en este momento.",

  "busy.short-wait": "Espera corta",
  "busy.busy": "Con gente",
  "busy.very-busy": "Espera larga",
  "busy.closed": "Cerrado",

  "queues.title": "Colas disponibles",
  "queues.subtitle": "Entra sin cita: estas son las esperas en este momento.",
  "queues.empty": "Las esperas en vivo no están disponibles en este momento.",
  "queues.join": "Ponerme en la fila",
  "queues.book": "Reservar una hora",
  "queues.minutes": "min",
  "queues.hours": "h",
  "queues.walk": "{n} min a pie",
  "queues.openUntil": "Abierto hasta las {t}",
  "queues.opensNext": "Abre a las {t}",
  "queues.ahead": "{n} por delante",
  "queues.more.one": "{n} mostrador más en este ámbito.",
  "queues.more.other": "{n} mostradores más en este ámbito.",
  "queues.sort.shortest-wait": "Menor espera primero",
  "queues.sort.name": "Por nombre",
  "queues.sort.location": "Por ubicación",
  "queues.sort.curated": "En el orden fijado aquí",

  "appointments.title": "¿Prefieres no esperar? Reserva una hora",
  "appointments.subtitle": "Las próximas horas libres de esta semana.",
  "appointments.empty": "No hay horas reservables en este momento.",
  "appointments.cta": "Reservar ahora",
  "appointments.none": "Sin horas libres en este periodo",
  "appointments.more.one": "{n} servicio más en este ámbito.",
  "appointments.more.other": "{n} servicios más en este ámbito.",

  "events.title": "Esta semana",
  "events.subtitle": "Talleres, ferias y sesiones abiertas de estos días.",
  "events.empty": "No hay nada programado en este periodo.",
  "events.featured.eyebrow": "Destacado",
  "events.featured.title": "No te lo pierdas",
  "events.featured.empty": "Ahora mismo no hay ningún evento destacado.",
  "events.all": "Todos los eventos",
  "events.cta": "Inscribirme",
  "events.more.one": "{n} evento más en este ámbito.",
  "events.more.other": "{n} eventos más en este ámbito.",

  "reg.open": "Inscripción abierta",
  "reg.closed": "Inscripción cerrada",
  "reg.full": "Completo",
  "reg.waitlist": "Lista de espera",
  "reg.waitlist.count": "Lista de espera — {n} en espera",
  "reg.not-required": "No hace falta inscribirse",
  "reg.counts": "{label} ({a}/{b})",
  "reg.countsOnly": "{label} ({a} inscritos)",

  "directory.title": "Ver departamentos",
  "directory.titleCount": "Ver los {n} departamentos",
  "directory.subtitle": "Si ya sabes a quién necesitas: cada departamento, qué reserva y cómo.",
  "directory.empty": "La lista de departamentos no está disponible en este momento.",
  "directory.show": "Ver",
  "directory.hide": "Ocultar",
  "directory.inside.one": "{n} dentro",
  "directory.inside.other": "{n} dentro",
  "directory.cap.walkIns": "Sin cita",
  "directory.cap.books": "Reservar",
  "directory.cap.events": "Eventos",
  "directory.more": "{n} más en esta parte de la organización.",

  "checklist.title": "Esta semana",
  "checklist.empty": "Por ahora no tienes nada pendiente.",
  "checklist.more": "Ver los {n}",
  "checklist.less": "Ver menos",
  "checklist.cta.join-queue": "Sin cita",
  "checklist.cta.book-appointment": "Reservar",
  "checklist.cta.register-event": "Inscribirme",

  "identity.eyebrow": "Ahora mismo",
  "identity.signedOut.title": "Inicia sesión para ver tu cuenta",
  "identity.signedOut.button": "Continuar e iniciar sesión",
  "identity.findVisit": "¿Ya estás en una fila? Busca tu visita →",
  "identity.greeting": "Hola de nuevo, {name}",
  "identity.greetingGeneric": "Hola de nuevo",
  "identity.ahead": "por delante",
  "identity.ticketCta": "Ver mi turno",
  "identity.visitsCta": "Mis visitas",
  "identity.degraded": "Algunos datos no están disponibles en este momento.",
  "identity.nextAppointment": "Próxima cita",
  "identity.ticket.waiting": "En la fila de {queue}",
  "identity.ticket.called": "Te han llamado en {queue}",
  "identity.ticket.being-served": "Te están atendiendo en {queue}",
  "identity.signOut": "Cerrar sesión",
  "identity.demo": "Demo",
  "identity.sessionEnded": "Tu sesión ha terminado. Vuelve a iniciar sesión.",

  "signin.title": "Iniciar sesión",
  "signin.chooser.body": "Elige con quién quieres iniciar sesión.",
  "signin.chooser.loading": "Cargando las opciones de inicio de sesión…",
  "signin.chooser.error": "No hemos podido cargar las opciones de inicio de sesión.",
  "signin.useEmail": "Iniciar sesión con mi correo electrónico",
  "signin.email.body": "Te enviaremos un código de un solo uso por correo.",
  "signin.email.field": "Correo electrónico",
  "signin.email.send": "Enviarme un código",
  "signin.email.sent": "Si esa dirección está registrada, el código va de camino. Caduca en {n} minutos.",
  "signin.email.code": "Escribe el código",
  "signin.email.submit": "Iniciar sesión",
  "signin.email.resend": "Enviar otro código",
  "signin.email.wait": "Puedes pedir otro código dentro de {n} s.",
  "signin.email.other": "Usar otra dirección",
  "signin.error.generic": "No se ha podido iniciar sesión.",
  "signin.error.code": "Ese código no es correcto o ha caducado. Pide uno nuevo.",
  "signin.error.unavailable": "Ese inicio de sesión no está disponible. Empieza de nuevo.",
  "signin.error.tooMany": "Demasiados intentos. Pide un código nuevo.",

  "myVisits.title": "Buscar mi visita",
  "myVisits.eyebrow": "¿Perdiste el mensaje?",
  "myVisits.body":
    "Busca lo que reservaste o la fila en la que te pusiste, con el número de teléfono que usaste.",
  "myVisits.field": "Número de móvil",
  "myVisits.submit": "Enviarme mis visitas",

  "flow.join-queue.title": "Ponerme en la fila",
  "flow.join-queue.cta": "Ponerme en la fila",
  "flow.book-appointment.title": "Reservar una hora",
  "flow.book-appointment.cta": "Reservar",
  "flow.register-event.title": "Inscribirme",
  "flow.register-event.cta": "Inscribirme",
  "flow.unconfigured": "Este bloque todavía no apunta a nada.",

  "faq.show": "Ver",
  "faq.hide": "Ocultar",

  "notice.dismiss": "Descartar",

  "cta.working": "Un momento…",
  "cta.retry": "Reintentar",
  "cta.error": "No se ha podido completar.",
  "cta.pending.join": "Ponerse en la fila llega en la próxima entrega",
  "cta.pending.book": "Reservar llega en la próxima entrega",
  "cta.pending.register": "Inscribirse llega en la próxima entrega",
  "cta.pending.lookup": "Buscar una visita llega en la próxima entrega",
  "cta.pending.signIn": "Iniciar sesión llega en la próxima entrega",
  "cta.preview": "No activo en la vista previa",
  "cta.offline": "No hemos podido contactar con el servidor.",

  "visitor.legend": "Tus datos",
  "visitor.firstName": "Nombre",
  "visitor.lastName": "Apellidos",
  "visitor.email": "Correo electrónico",
  "visitor.phone": "Número de móvil",
  "visitor.phoneWhy": "Para avisarte por SMS cuando sea tu turno.",
  "visitor.optional": "Opcional",
  "visitor.incomplete": "Añade tu nombre y tu correo para continuar.",
  "visitor.incompleteName": "Añade tu nombre para continuar.",
  "visitor.error.firstName": "Escribe tu nombre.",
  "visitor.error.lastName": "Escribe tus apellidos.",
  "visitor.error.emailRequired": "Escribe tu correo electrónico.",
  "visitor.error.email": "Eso no parece una dirección de correo electrónico.",
  "visitor.error.phone": "Eso no parece un número de teléfono.",
  "visitor.cancel": "Cancelar",

  "queue.joined.eyebrow": "Estás en la fila",
  "queue.joined.ticket": "Turno {ref}",
  "queue.joined.ahead": "por delante de ti",
  "queue.joined.wait": "Unos {n} min",
  "queue.joined.code": "Código de entrada {code}",
  "queue.already": "Ya estás en esta fila.",
  "queue.closed": "Este mostrador ha cerrado.",
  "queue.leave": "Salir de la fila",
  "book.hold.eyebrow": "Hora reservada para ti",
  "book.holdUntil": "Reservada hasta las {t}",
  "book.holdLeft": "quedan {t}",
  "book.confirm": "Confirmar esta hora",
  "book.confirmed": "Cita confirmada",
  "book.taken": "Esa hora se acaba de ocupar.",
  "book.expired": "Tu reserva ha caducado.",
  "book.maybeBooked": "Puede que ya tengamos tu cita: revisa tu correo.",
  "book.pickAnother": "Elegir otra hora",
  "book.otherTimes": "Otras horas de ese día",
  "book.noOtherTimes": "No queda nada libre ese día.",
  "event.registered": "Ya estás inscrito",
  /* LOCAL PATCH (platform side, 2026-10-04 JAI-2279): neutral duplicate-registration copy. CD to absorb. */
  "event.alreadyRegistered": "Ya estabas inscrito",
  "event.alreadyRegistered.body": "Ya estabas inscrito en este evento. Revisa tu correo electrónico para ver tu confirmación.",
  "ref": "Referencia {ref}",
  "ref.manage.aria": "Gestionar la reserva {ref} (se abre en una pestaña nueva)",
  "copy.action": "Copiar",
  "copy.done": "Copiado",
  "copy.ticket": "Copiar el turno {ref}",
  "copy.code": "Copiar el código de entrada {code}",
  "copy.ref": "Copiar la referencia {ref}",

  "handoff.title": "Continuar con la reserva",
  "handoff.body.session": "Tu reserva se abre en una pestaña nueva, con tu sesión iniciada. Esta página sigue abierta.",
  "handoff.body.guest": "Tu reserva se abre en una pestaña nueva. Esta página sigue abierta.",
  "handoff.continue": "Continuar con la reserva",
  "handoff.preparing": "Preparando tu reserva…",
  "handoff.opened": "Tu reserva está abierta en la otra pestaña.",
  "handoff.expired": "Ese enlace de reserva caducó antes de usarse.",
  "handoff.new": "Obtener un enlace nuevo",
  "handoff.error": "La reserva no está disponible en este momento.",
  "handoff.error.wait": "Demasiados intentos. Inténtalo de nuevo en {n}s.",

  "block.unsupported.title": "Bloque no compatible",
  "block.unsupported.body":
    "Este tipo de bloque no tiene componente, así que no aparecerá en la página publicada.",
};

/** ui-config — one entry per shipped locale. */
const CATALOG: Record<string, Partial<Record<StringKey, string>>> = {
  "en-US": EN,
  "es-US": ES,
  es: ES,
  // JAI-1154 item 1. `de-CH` is LAYERED over `de` (Swiss deltas only) — the
  // resolver's base-language step is what completes it, so no key can fall
  // through to English while `de` carries it. See `strings-eu.ts`.
  de: DE,
  "de-CH": DE_CH,
  fr: FR,
  it: IT,
};

/* ── resolution ──────────────────────────────────────────────────────── */

/**
 * The resolved catalog for one locale.
 *
 * It is a CALLABLE with properties rather than a bare function because plural
 * selection and number formatting are locale-dependent, and every existing call
 * site passes `t` around rather than the locale string. Consumers that only call
 * `t(key, params)` are unaffected.
 */
export interface Translate {
  (key: StringKey, params?: Record<string, string | number>): string;
  /** The locale this catalog was resolved for. */
  locale: string;
  /** The CLDR plural category of `n` in that locale. */
  category: (n: number) => string;
  /** `Intl.NumberFormat` in that locale. */
  number: (n: number) => string;
}

/**
 * Interpolate `{name}` holes.
 *
 * ⛔ EVERY NUMERIC PARAM IS FORMATTED FOR THE LOCALE, here, once. `String(1234)`
 * is `"1234"` in every language, and the pages this catalog serves are Swiss:
 * `1234` must read `1234` in `de-CH` and `1 234` in `fr-CH`. Doing it at the
 * interpolation site means the ~40 call sites that pass `{n}`, `{a}`, `{b}` are
 * all correct without any of them changing — and a ratio like `{a}/{b}` stays
 * ONE key with two formatted numbers rather than two numbers concatenated in
 * English word order.
 */
function interpolate(
  template: string,
  params: Record<string, string | number> | undefined,
  format: (n: number) => string,
): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/gu, (whole, name: string) => {
    if (!Object.prototype.hasOwnProperty.call(params, name)) return whole;
    const value = params[name];
    return typeof value === "number" ? format(value) : String(value);
  });
}

/**
 * Resolve chrome strings for one document locale. Order: the exact tag, then
 * its base language, then any regional variant of that language, then `en-US`.
 * A locale the catalog has never heard of therefore renders readable English
 * rather than raw keys — a fallback, never a claim to have translated.
 */
export function uiStrings(locale: string): Translate {
  const base = locale.split("-")[0] ?? locale;
  const variant = Object.keys(CATALOG).find((k) => k !== locale && k.startsWith(`${base}-`));
  const chain = [CATALOG[locale], CATALOG[base], variant ? CATALOG[variant] : undefined, EN];

  // Built once per resolution, and defensively: an `Intl` that rejects the tag
  // (an unknown or malformed locale reaching a browser that is strict about it)
  // must degrade to English formatting, never throw during a render.
  let rules: Intl.PluralRules;
  let numbers: Intl.NumberFormat;
  try {
    rules = new Intl.PluralRules(locale);
    numbers = new Intl.NumberFormat(locale);
  } catch {
    rules = new Intl.PluralRules("en");
    numbers = new Intl.NumberFormat("en");
  }
  const number = (n: number) => numbers.format(n);

  const translate = ((key: StringKey, params?: Record<string, string | number>) => {
    for (const table of chain) {
      const value = table?.[key];
      if (typeof value === "string" && value) return interpolate(value, params, number);
    }
    return key;
  }) as Translate;

  translate.locale = locale;
  translate.category = (n: number) => rules.select(n);
  translate.number = number;
  return translate;
}

/**
 * Pick the plural sibling of a key BY CLDR CATEGORY (JAI-1154 item 2).
 *
 * ⛔ NOT `n === 1`. That split is right for English and Spanish and wrong for the
 * locales this catalog just gained and the ones it will gain next: French puts 0
 * in `one`, so "0 résultat" — and Russian and Ukrainian put 21 and 31 there, so a
 * count of 21 would silently take the `other` sentence. `Intl.PluralRules`
 * carries the real table, and `other` stays the required fallback so a category a
 * catalog does not spell (`few`, `many`, `two`) still renders a sentence rather
 * than a key.
 *
 * The count is formatted by `interpolate` for the same locale, so a plural and a
 * number never disagree about which language they are in.
 */
export function plural(t: Translate, key: string, n: number, params?: Record<string, string | number>): string {
  const category = t.category(n);
  const specific = `${key}.${category}` as StringKey;
  const resolved = t(specific, { n, ...params });
  // `t` answers the KEY ITSELF when nothing in the chain carries it — that is
  // the signal to fall back to `other` rather than print `queues.more.few`.
  return resolved === specific ? t(`${key}.other` as StringKey, { n, ...params }) : resolved;
}
